import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaCrosshairs,
  FaEnvelope,
  FaExternalLinkAlt,
  FaFilter,
  FaGlobe,
  FaGlobeEurope,
  FaMapMarkerAlt,
  FaPhone,
  FaRoute,
  FaSearch,
  FaTimes,
  FaUserTie,
} from "react-icons/fa";

import useScrollToTop from "../../../hooks/useScrollToTop";
import Reveal from "../../../components/ui/Reveal";
import SectionTitle from "../HomePage/SectionTitle";

import { getAllSections } from "../../../services/sectionsServices";
import { getAllCategoriesAsso } from "../../../services/categoriesAssoServices";
import { resolveMediaUrl, truncateText } from "../HomePage/homeUtils";

const DEFAULT_MAP_QUERY = "Le Bouscat 33110";

const normalizeText = (value = "") =>
  String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const cleanPhone = (phone = "") => String(phone).replace(/[^\d+]/g, "");

const buildGoogleMapEmbedUrl = (address) => {
  const query = address || DEFAULT_MAP_QUERY;
  return `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
};

const buildGoogleMapLink = (address) => {
  const query = address || DEFAULT_MAP_QUERY;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query
  )}`;
};

const getCategoryLabel = (section, categoriesById) => {
  return categoriesById[String(section.category_id)]?.label || "Non catégorisée";
};

const MapPage = () => {
  useScrollToTop();

  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [filteredCategoryId, setFilteredCategoryId] = useState("");
  const [selectedAssociationId, setSelectedAssociationId] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);

        const [sectionsData, categoriesData] = await Promise.all([
          getAllSections(),
          getAllCategoriesAsso(),
        ]);

        const activeSections = Array.isArray(sectionsData)
          ? sectionsData
              .filter((section) => section.is_active !== false)
              .sort((a, b) =>
                String(a.nom_section || "").localeCompare(
                  String(b.nom_section || ""),
                  "fr",
                  { sensitivity: "base" }
                )
              )
          : [];

        const activeCategories = Array.isArray(categoriesData)
          ? categoriesData
              .filter((category) => category.is_active !== false)
              .sort((a, b) => {
                const orderA = Number(a.ordre ?? 999);
                const orderB = Number(b.ordre ?? 999);

                if (orderA !== orderB) return orderA - orderB;

                return String(a.label || "").localeCompare(
                  String(b.label || ""),
                  "fr",
                  { sensitivity: "base" }
                );
              })
          : [];

        if (!mounted) return;

        setSections(activeSections);
        setCategories(activeCategories);

        const firstWithAddress = activeSections.find(
          (section) => section.adresse_section
        );

        if (firstWithAddress) {
          setSelectedAssociationId(String(firstWithAddress.id));
        }
      } catch (error) {
        console.error("Erreur lors du chargement de la carte:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  const categoriesById = useMemo(() => {
    return categories.reduce((acc, category) => {
      acc[String(category.id)] = category;
      return acc;
    }, {});
  }, [categories]);

  const filteredSections = useMemo(() => {
    const q = normalizeText(query.trim());

    return sections.filter((section) => {
      const categoryLabel = getCategoryLabel(section, categoriesById);

      const searchableText = [
        section.nom_section,
        section.discipline_section,
        section.responsable_section,
        section.adresse_section,
        categoryLabel,
      ]
        .filter(Boolean)
        .join(" ");

      const matchesQuery = q
        ? normalizeText(searchableText).includes(q)
        : true;

      const matchesCategory = filteredCategoryId
        ? String(section.category_id) === String(filteredCategoryId)
        : true;

      return matchesQuery && matchesCategory;
    });
  }, [sections, query, filteredCategoryId, categoriesById]);

  const selectedAssociation = useMemo(() => {
    return (
      sections.find(
        (section) => String(section.id) === String(selectedAssociationId)
      ) || null
    );
  }, [sections, selectedAssociationId]);

  const mapAddress =
    selectedAssociation?.adresse_section ||
    filteredSections.find((section) => section.adresse_section)?.adresse_section ||
    DEFAULT_MAP_QUERY;

  const mapUrl = buildGoogleMapEmbedUrl(mapAddress);
  const mapsLink = buildGoogleMapLink(mapAddress);

  const resetFilters = () => {
    setQuery("");
    setFilteredCategoryId("");

    const firstWithAddress = sections.find((section) => section.adresse_section);
    setSelectedAssociationId(firstWithAddress ? String(firstWithAddress.id) : "");
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-white">
      <div className="h-[70px] w-full bg-slate-950" />

      <main>
        <section className="relative overflow-hidden bg-slate-950 py-20 sm:py-24">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(34,197,94,0.2),transparent_35%),radial-gradient(circle_at_80%_35%,rgba(59,130,246,0.25),transparent_40%)]" />

          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
              <Reveal>
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-bold text-white/90 backdrop-blur">
                    <FaGlobeEurope />
                    Carte interactive
                  </span>

                  <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                    Les associations sur la carte
                  </h1>

                  <p className="mt-6 max-w-3xl text-base leading-8 text-white/75 sm:text-lg">
                    Recherchez une association, filtrez par catégorie et affichez automatiquement son adresse sur la carte.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={0.12}>
                <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
                  <HeroStat
                    label="Associations"
                    value={sections.length}
                    icon={FaMapMarkerAlt}
                  />
                  <HeroStat
                    label="Affichées"
                    value={filteredSections.length}
                    icon={FaFilter}
                  />
                  <HeroStat
                    label="Avec adresse"
                    value={sections.filter((section) => section.adresse_section).length}
                    icon={FaCrosshairs}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 dark:bg-slate-900 sm:py-20">
          <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8">
            <SectionTitle
              eyebrow="Explorer"
              title="Trouvez une "
              highlight="association"
              subtitle="Sélectionnez une association dans la liste : la carte se mettra automatiquement à jour avec son adresse."
            />

            <div className="grid gap-6 xl:grid-cols-[390px_minmax(0,1fr)]">
              <Reveal>
                <aside className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-50 shadow-sm dark:border-white/10 dark:bg-slate-950">
                  <div className="border-b border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-slate-900">
                    <h2 className="text-xl font-bold text-slate-950 dark:text-white">
                      Rechercher
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-300">
                      Filtrez les associations et cliquez sur une fiche pour afficher son emplacement.
                    </p>
                  </div>

                  <div className="space-y-4 p-5">
                    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-900">
                      <FaSearch className="text-slate-400" />
                      <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Rechercher une association..."
                        className="w-full bg-transparent text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400 dark:text-white"
                      />
                    </div>

                    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-900">
                      <FaFilter className="text-primary" />
                      <select
                        value={filteredCategoryId}
                        onChange={(e) => setFilteredCategoryId(e.target.value)}
                        className="w-full bg-transparent text-sm font-bold text-slate-700 outline-none dark:text-white"
                      >
                        <option value="">Toutes les catégories</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-900">
                      <FaMapMarkerAlt className="text-primary" />
                      <select
                        value={selectedAssociationId}
                        onChange={(e) => setSelectedAssociationId(e.target.value)}
                        className="w-full bg-transparent text-sm font-bold text-slate-700 outline-none dark:text-white"
                      >
                        <option value="">Choisir une association</option>
                        {filteredSections.map((section) => (
                          <option key={section.id} value={section.id}>
                            {section.nom_section}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="button"
                      onClick={resetFilters}
                      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:text-primary dark:border-white/10 dark:bg-slate-900 dark:text-white"
                    >
                      <FaTimes />
                      Réinitialiser
                    </button>

                    <div className="border-t border-slate-200 pt-4 dark:border-white/10">
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                        Résultats
                      </p>
                      <p className="mt-1 text-sm font-bold text-slate-700 dark:text-slate-200">
                        {filteredSections.length} association
                        {filteredSections.length > 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>

                  <div className="max-h-[720px] space-y-3 overflow-y-auto px-5 pb-5">
                    {loading ? (
                      <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
                        Chargement des associations...
                      </div>
                    ) : filteredSections.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
                        Aucune association trouvée.
                      </div>
                    ) : (
                      filteredSections.map((section) => {
                        const categoryLabel = getCategoryLabel(
                          section,
                          categoriesById
                        );
                        const isSelected =
                          String(selectedAssociationId) === String(section.id);

                        return (
                          <button
                            key={section.id}
                            type="button"
                            onClick={() =>
                              setSelectedAssociationId(String(section.id))
                            }
                            className={[
                              "w-full overflow-hidden rounded-[1.5rem] border p-4 text-left transition",
                              isSelected
                                ? "border-primary bg-primary/5 shadow-lg"
                                : "border-slate-200 bg-white hover:-translate-y-0.5 hover:shadow-lg dark:border-white/10 dark:bg-slate-900",
                            ].join(" ")}
                          >
                            <div className="flex gap-4">
                              <div className="h-20 w-20 shrink-0 overflow-hidden rounded-2xl bg-slate-100 dark:bg-slate-800">
                                <img
                                  src={resolveMediaUrl(
                                    section.photoURL,
                                    "/img/placeholder-association.jpg"
                                  )}
                                  alt={section.nom_section}
                                  className="h-full w-full object-cover"
                                />
                              </div>

                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-primary">
                                    {categoryLabel}
                                  </span>

                                  {!section.adresse_section && (
                                    <span className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                                      Adresse manquante
                                    </span>
                                  )}
                                </div>

                                <h3 className="mt-2 text-base font-bold text-slate-950 dark:text-white">
                                  {section.nom_section}
                                </h3>

                                {section.discipline_section && (
                                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                                    {section.discipline_section}
                                  </p>
                                )}

                                {section.adresse_section && (
                                  <p className="mt-2 line-clamp-2 text-xs leading-5 text-slate-500 dark:text-slate-300">
                                    {section.adresse_section}
                                  </p>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </aside>
              </Reveal>

              <Reveal delay={0.08}>
                <div className="grid gap-6">
                  <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-50 shadow-sm dark:border-white/10 dark:bg-slate-950">
                    <div className="border-b border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-slate-900">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <h2 className="text-xl font-bold text-slate-950 dark:text-white">
                            Carte
                          </h2>
                          <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                            {selectedAssociation
                              ? selectedAssociation.adresse_section ||
                                "Adresse non renseignée"
                              : "Sélectionnez une association pour afficher sa position."}
                          </p>
                        </div>

                        <a
                          href={mapsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-3 text-sm font-bold text-white shadow transition hover:-translate-y-0.5 hover:shadow-lg"
                        >
                          <FaRoute />
                          Ouvrir l’itinéraire
                        </a>
                      </div>
                    </div>

                    <div className="h-[620px] w-full bg-slate-100 dark:bg-slate-950">
                      <iframe
                        key={mapUrl}
                        title="Carte des associations"
                        src={mapUrl}
                        className="h-full w-full"
                        style={{ border: 0 }}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                  </div>

                  <AssociationDetail
                    association={selectedAssociation}
                    categoriesById={categoriesById}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

function AssociationDetail({ association, categoriesById }) {
  if (!association) {
    return (
      <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
        Sélectionnez une association pour afficher sa fiche.
      </div>
    );
  }

  const categoryLabel = getCategoryLabel(association, categoriesById);
  const phone = cleanPhone(association.tel_section);

  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-5 dark:border-white/10">
        <h2 className="text-xl font-bold text-slate-950 dark:text-white">
          Détail de l’association
        </h2>
      </div>

      <div className="grid gap-6 p-5 lg:grid-cols-[320px_1fr]">
        <div className="overflow-hidden rounded-[1.5rem] bg-slate-100 dark:bg-slate-950">
          <img
            src={resolveMediaUrl(
              association.photoURL,
              "/img/placeholder-association.jpg"
            )}
            alt={association.nom_section}
            className="h-full min-h-[260px] w-full object-cover"
          />
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
              {categoryLabel}
            </span>

            {association.discipline_section && (
              <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-600 dark:bg-white/10 dark:text-slate-300">
                {association.discipline_section}
              </span>
            )}
          </div>

          <h3 className="mt-4 text-3xl font-bold text-slate-950 dark:text-white">
            {association.nom_section}
          </h3>

          {association.about_content && (
            <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
              {truncateText(association.about_content, 320)}
            </p>
          )}

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {association.adresse_section && (
              <InfoCard
                icon={FaMapMarkerAlt}
                label="Adresse"
                value={association.adresse_section}
              />
            )}

            {association.responsable_section && (
              <InfoCard
                icon={FaUserTie}
                label="Responsable"
                value={association.responsable_section}
              />
            )}

            {association.tel_section && (
              <InfoCard
                icon={FaPhone}
                label="Téléphone"
                value={association.tel_section}
              />
            )}

            {association.mail_section && (
              <InfoCard
                icon={FaEnvelope}
                label="Email"
                value={association.mail_section}
              />
            )}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {association.lien_section && (
              <Link
                to={association.lien_section}
                className="inline-flex items-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-bold text-white shadow transition hover:-translate-y-0.5 hover:shadow-lg"
              >
                Voir la fiche
                <FaExternalLinkAlt className="text-xs" />
              </Link>
            )}

            {association.siteweb_section && (
              <a
                href={association.siteweb_section}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:text-primary dark:border-white/10 dark:bg-slate-950 dark:text-white"
              >
                <FaGlobe />
                Site web
              </a>
            )}

            {phone && (
              <a
                href={`tel:${phone}`}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:text-primary dark:border-white/10 dark:bg-slate-950 dark:text-white"
              >
                <FaPhone />
                Appeler
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-3xl border border-white/15 bg-white/10 p-5 shadow-2xl backdrop-blur-md">
      <div className="flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-primary shadow">
          <Icon />
        </span>

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wide text-white/55">
            {label}
          </p>
          <p className="mt-1 truncate text-lg font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950">
      <div className="flex gap-3">
        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon />
        </span>

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
            {label}
          </p>
          <p className="mt-1 break-words text-sm font-bold text-slate-700 dark:text-slate-200">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

export default MapPage;